import { v4 as uuidv4 } from 'uuid';
import { databaseService } from '../databaseService';
import { APP_CONSTANTS } from '../../config/constants';
import { isGpsAddressField } from './gpsAddress';
import { canCreateGpsAddressField } from './gpsAddressCapability';
import { getLogger } from '../../utils/logger';
import {
  ChecklistStructuralMutationService,
  type ChecklistStructuralMutationDatabase,
} from './ChecklistStructuralMutationService';

// Logger para o serviço de templates de checklist
const logger = getLogger('CHECKLIST-SERVICE');

// Array completo de campos do template padrão (copie do plano/documentacao)
// REMOVIDO: const defaultVehicleFields = [...]

export class ChecklistTemplateService {
  constructor(
    private readonly structuralMutations = new ChecklistStructuralMutationService(),
    private readonly database: ChecklistStructuralMutationDatabase = databaseService,
    private readonly gpsCreationEnabled = canCreateGpsAddressField
  ) {}

  async createDefaultVehicleTemplate(empresaId: string, numFotos: number = 5): Promise<string> {
    this.structuralMutations.assertAllowed('template', 'create');
    return this.createDefaultVehicleTemplateInternal(empresaId, numFotos, false);
  }

  // O cadastro inicial tem contrato próprio porque pode ocorrer antes da sessão completa.
  async createDefaultVehicleTemplateForBootstrap(
    empresaId: string,
    numFotos: number = 5
  ): Promise<string> {
    return this.createDefaultVehicleTemplateInternal(empresaId, numFotos, true);
  }

  private async createDefaultVehicleTemplateInternal(
    empresaId: string,
    numFotos: number,
    isBootstrap: boolean
  ): Promise<string> {

    // Criar o template principal
    const templateId = uuidv4();
    // Criar campos conforme modelo dinâmico
    const now = new Date().toISOString();

    const fields = getDefaultVehicleFieldsWithPhotos(numFotos);
    const fieldRecords = fields.map(field => ({
      id: uuidv4(),
      empresa_id: empresaId,
      template_id: templateId,
      versao: 1,
      grupo: field.grupo,
      nome: field.nome,
      tipo: field.tipo,
      obrigatorio: field.obrigatorio ? 1 : 0,
      deletavel: field.deletavel ? 1 : 0,
      alteravel: field.alteravel ? 1 : 0,
      visivel: field.visivel ? 1 : 0,
      ordem: field.ordem ?? 0,
      created_at: now,
      updated_at: now,
    }));

    logger.info(`Criando template principal para empresa ${empresaId} com ID: ${templateId}`);
    if (isBootstrap) {
      await this.database.createItem(
        APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME,
        {
          id: templateId,
          empresa_id: empresaId,
          nome: 'Carros',
          versao: 1,
          created_at: now,
          updated_at: now
        }
      );
    } else {
      await this.structuralMutations.createTemplate({
        id: templateId,
        empresaId,
        nome: 'Carros',
        versao: 1,
      });
    }

    logger.info(`Criando campos para o template ${templateId}`);
    // Vamos armazenar campos do tipo "marcação" para criar as imagens default depois
    const marcacaoFields: { id: string; nome: string }[] = [];
    logger.info(`Iniciando loop de criação dos campos do template. Total de campos: ${fields.length}`);
    if (isBootstrap) {
      for (const field of fieldRecords) {
        logger.info(`[Campo] Criando campo '${field.nome}' (tipo: ${field.tipo}) com id ${field.id}`);
        await this.database.createItem(
          APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME,
          field
        );
        if (field.tipo === 'marcação') {
          logger.info(`[Campo] Campo de marcação identificado: '${field.nome}' (id: ${field.id})`);
          marcacaoFields.push({ id: field.id, nome: field.nome });
        }
      }

      const versionUpdated = await this.database.updateItem(
        APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME,
        templateId,
        { versao: 1 + fieldRecords.length },
        true
      );
      if (!versionUpdated) {
        throw new Error(`Não foi possível reservar a versão estrutural do bootstrap ${templateId}.`);
      }
    } else {
      await this.structuralMutations.createFields(templateId, fieldRecords);
      fieldRecords.forEach(field => {
        if (field.tipo === 'marcação') {
          marcacaoFields.push({ id: field.id, nome: field.nome });
        }
      });
    }

    logger.info(`Template ${templateId} criado com sucesso`);

    if (marcacaoFields.length) {
      logger.info(`[Imagens] Criação de imagens padrão adiada para o fluxo normal de sync. Total: ${marcacaoFields.length}`);
    }

    return templateId;
  }

  async duplicateTemplate(origTemplateId: string, empresaId: string, newName: string): Promise<string> {
    logger.info(`[duplicateTemplate] Iniciando duplicação de template. origTemplateId=${origTemplateId}, empresaId=${empresaId}, newName=${newName}`);
    this.structuralMutations.assertAllowed('template', 'create');
    const now = new Date().toISOString();
    const newTemplateId = uuidv4();

    // 1) Ler template original para obter versao
    logger.info(`[duplicateTemplate] Buscando template original no banco local: ${origTemplateId}`);
    const origTemplate = await this.database.readItem<any>(
      APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME,
      origTemplateId
    );

    const versao = origTemplate?.versao ?? 1;

    // Conferir o recurso antes de criar o cabeçalho evita uma cópia incompleta quando o piloto está desligado.
    const camposOrig = await this.database.listItems<any>(
      APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME,
      { template_id: origTemplateId, empresa_id: empresaId }
    );
    const camposAtivos = (camposOrig ?? []).filter(campo => !campo.retired_at);
    if (camposAtivos.some(isGpsAddressField) && !await this.gpsCreationEnabled(empresaId)) {
      throw new Error('O endereço GPS não está habilitado para duplicar este modelo.');
    }
    await this.structuralMutations.createTemplate({
      id: newTemplateId,
      empresaId,
      nome: newName,
      versao,
    });

    const batchInsert: any[] = [];
    const fieldMappings: Array<{ original: any; copy: any }> = [];
    if (camposAtivos.length) {
      logger.info(`[duplicateTemplate] Criando campos localmente para o novo template: ${newTemplateId}`);
      for (const campo of camposAtivos) {
        const copiedField = {
          id: uuidv4(),
          empresa_id: empresaId,
          template_id: newTemplateId,
          versao: campo.versao,
          grupo: campo.grupo,
          nome: campo.nome,
          tipo: campo.tipo,
          ...(campo.endereco_gps === 1 || campo.endereco_gps === true ? { endereco_gps: 1 } : {}),
          obrigatorio: campo.obrigatorio,
          deletavel: campo.deletavel,
          alteravel: campo.alteravel,
          visivel: campo.visivel,
          ordem: campo.ordem,
          created_at: now,
          updated_at: now,
        };
        batchInsert.push(copiedField);
        fieldMappings.push({ original: campo, copy: copiedField });
      }

      await this.structuralMutations.createFields(newTemplateId, batchInsert);
    }

    for (const { original, copy } of fieldMappings) {
      if (original.tipo !== 'marcação') continue;
      const origImgs = await this.database.listItems<any>(
        APP_CONSTANTS.DATABASE.TABLES.GUINCHO_MARCACAO_IMAGENS.NAME,
        { campo_id: original.id }
      );
      const origImg = origImgs?.find((image: any) => !image.retired_at && image.foto_url);
      if (!origImg?.foto_url) continue;

      await this.database.createItem(
        APP_CONSTANTS.DATABASE.TABLES.GUINCHO_MARCACAO_IMAGENS.NAME,
        {
          id: uuidv4(),
          empresa_id: empresaId,
          template_id: newTemplateId,
          campo_id: copy.id,
          foto_url: origImg.foto_url,
          created_at: now,
          updated_at: now,
        }
      );
    }

    logger.info(`[duplicateTemplate] Imagens de marcação do template duplicado registradas localmente para sync normal: ${newTemplateId}`);

    logger.info(`[duplicateTemplate] Finalizada duplicação de template. newTemplateId=${newTemplateId}`);
    logger.info(`Template duplicado ${origTemplateId} -> ${newTemplateId}`);
    return newTemplateId;
  }
}

/**
 * Gera dinamicamente os campos de foto para o checklist, conforme o número máximo definido pela empresa.
 * @param max Quantidade máxima de fotos permitidas
 * @param startOrder Ordem inicial para o primeiro campo de foto (default: 1)
 * @returns Array de campos do tipo 'foto' para o grupo 07 - Fotos
 */
export function generatePhotoFields(max: number, startOrder: number = 1) {
  // Garante pelo menos 1 foto
  const total = Math.max(1, max || 1);
  logger.info?.(`[generatePhotoFields] Gerando ${total} campos de foto a partir da ordem ${startOrder}`);
  return Array.from({ length: total }, (_, i) => ({
    grupo: '07 - Fotos',
    nome: `Foto ${i + 1}`,
    tipo: 'foto',
    obrigatorio: false,
    deletavel: false,
    alteravel: true,
    visivel: true,
    ordem: startOrder + i,
  }));
}

// Array base dos campos do template padrão (sem as fotos)
export const defaultVehicleFieldsBase = [
  // 01 - Atendimento
  { grupo: "01 - Atendimento", nome: "Horário", tipo: "timestamp with time zone", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 1 },
  { grupo: "01 - Atendimento", nome: "Assistência", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 2 },
  { grupo: "01 - Atendimento", nome: "Código do Sinistro", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 3 },
  { grupo: "01 - Atendimento", nome: "Número B.O.", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 4 },
  { grupo: "01 - Atendimento", nome: "O cliente acompanhou o trajeto.", tipo: "mensagem pronta", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 5 },
  { grupo: "01 - Atendimento", nome: "Observações", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 6 },
  { grupo: "01 - Atendimento", nome: "Motorista", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 7 },
  { grupo: "01 - Atendimento", nome: "Seguradora", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: false, ordem: 8 },
  { grupo: "01 - Atendimento", nome: "CEP", tipo: "cep1", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 9 },
  { grupo: "01 - Atendimento", nome: "Endereço", tipo: "cep2", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 10 },
  { grupo: "01 - Atendimento", nome: "Número", tipo: "cep3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 11 },
  { grupo: "01 - Atendimento", nome: "Bairro", tipo: "cep4", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 12 },
  { grupo: "01 - Atendimento", nome: "Cidade", tipo: "cep5", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 13 },
  { grupo: "01 - Atendimento", nome: "UF", tipo: "cep6", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 14 },
  { grupo: "01 - Atendimento", nome: "Valor do serviço", tipo: "number", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 15 },
  // 02 - Veículo
  { grupo: "02 - Veículo", nome: "Ano", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 16 },
  { grupo: "02 - Veículo", nome: "KM", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 17 },
  { grupo: "02 - Veículo", nome: "Chassi", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 18 },
  { grupo: "02 - Veículo", nome: "Marca", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 19 },
  { grupo: "02 - Veículo", nome: "Modelo", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 20 },
  { grupo: "02 - Veículo", nome: "Cor", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 21 },
  { grupo: "02 - Veículo", nome: "Placa", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 22 },
  // 03 - Cliente
  { grupo: "03 - Cliente", nome: "Nome", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 23 },
  { grupo: "03 - Cliente", nome: "CPF/CNPJ", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 24 },
  { grupo: "03 - Cliente", nome: "Email", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 25 },
  { grupo: "03 - Cliente", nome: "Telefone", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 26 },
  { grupo: "03 - Cliente", nome: "Assinatura do Cliente", tipo: "assinatura", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 27 },
  { grupo: "03 - Cliente", nome: "Data e hora da assinatura", tipo: "timestamp with time zone", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 28 },
  // 04 - Acessórios
  { grupo: "04 - Acessórios", nome: "alarme", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 29 },
  { grupo: "04 - Acessórios", nome: "antena", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 30 },
  { grupo: "04 - Acessórios", nome: "bateria", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 31 },
  { grupo: "04 - Acessórios", nome: "banco traseiro", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 32 },
  { grupo: "04 - Acessórios", nome: "brake light", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 33 },
  { grupo: "04 - Acessórios", nome: "calotas", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 34 },
  { grupo: "04 - Acessórios", nome: "caixa selada", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 35 },
  { grupo: "04 - Acessórios", nome: "chave de roda", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 36 },
  { grupo: "04 - Acessórios", nome: "chaves", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 37 },
  { grupo: "04 - Acessórios", nome: "console int.", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 38 },
  { grupo: "04 - Acessórios", nome: "documentos", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 39 },
  { grupo: "04 - Acessórios", nome: "estepe", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 40 },
  { grupo: "04 - Acessórios", nome: "farois aux.", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 41 },
  { grupo: "04 - Acessórios", nome: "limpador traseiro", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 42 },
  { grupo: "04 - Acessórios", nome: "macaco", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 43 },
  { grupo: "04 - Acessórios", nome: "modulo potência", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 44 },
  { grupo: "04 - Acessórios", nome: "rádio", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 45 },
  { grupo: "04 - Acessórios", nome: "reboque engate", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 46 },
  { grupo: "04 - Acessórios", nome: "retrovisor elétrico", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 47 },
  { grupo: "04 - Acessórios", nome: "rodas de liga leve", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 48 },
  { grupo: "04 - Acessórios", nome: "tampao traseiro", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 49 },
  { grupo: "04 - Acessórios", nome: "tapetes", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 50 },
  { grupo: "04 - Acessórios", nome: "triângulo", tipo: "dropdown3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 51 },
  // 05 - Inspeção
  { grupo: "05 - Inspeção", nome: "Frente", tipo: "marcação", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 52 },
  { grupo: "05 - Inspeção", nome: "Traseira", tipo: "marcação", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 53 },
  { grupo: "05 - Inspeção", nome: "Lado Direito", tipo: "marcação", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 54 },
  { grupo: "05 - Inspeção", nome: "Lado Esquerdo", tipo: "marcação", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 55 },
  { grupo: "05 - Inspeção", nome: "Topo", tipo: "marcação", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 56 },
  { grupo: "05 - Inspeção", nome: "Nível Combustível", tipo: "dropdown1", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 57 },
  { grupo: "05 - Inspeção", nome: "Pneu Dianteiro Esquerdo (Marca)", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 58 },
  { grupo: "05 - Inspeção", nome: "Pneu Dianteiro Esquerdo (Estado)", tipo: "dropdown2", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 59 },
  { grupo: "05 - Inspeção", nome: "Pneu Dianteiro Direito (Marca)", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 60 },
  { grupo: "05 - Inspeção", nome: "Pneu Dianteiro Direito (Estado)", tipo: "dropdown2", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 61 },
  { grupo: "05 - Inspeção", nome: "Pneu Traseiro Esquerdo (Marca)", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 62 },
  { grupo: "05 - Inspeção", nome: "Pneu Traseiro Esquerdo (Estado)", tipo: "dropdown2", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 63 },
  { grupo: "05 - Inspeção", nome: "Pneu Traseiro Direito (Marca)", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 64 },
  { grupo: "05 - Inspeção", nome: "Pneu Traseiro Direito (Estado)", tipo: "dropdown2", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 65 },
  { grupo: "05 - Inspeção", nome: "Pneu Estepe (Marca)", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 66 },
  { grupo: "05 - Inspeção", nome: "Pneu Estepe (Estado)", tipo: "dropdown2", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 67 },
  // 06 - Destino
  { grupo: "06 - Destino", nome: "Data e Hora Destino", tipo: "timestamp with time zone", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 68 },
  { grupo: "06 - Destino", nome: "Nome", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 69 },
  { grupo: "06 - Destino", nome: "CPF/CNPJ", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 70 },
  { grupo: "06 - Destino", nome: "CEP", tipo: "cep1", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 71 },
  { grupo: "06 - Destino", nome: "Endereço", tipo: "cep2", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 72 },
  { grupo: "06 - Destino", nome: "Número", tipo: "cep3", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 73 },
  { grupo: "06 - Destino", nome: "Bairro", tipo: "cep4", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 74 },
  { grupo: "06 - Destino", nome: "Cidade", tipo: "cep5", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 75 },
  { grupo: "06 - Destino", nome: "UF", tipo: "cep6", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 76 },
  { grupo: "06 - Destino", nome: "Telefone", tipo: "text", obrigatorio: false, deletavel: true, alteravel: true, visivel: false, ordem: 77 },
  { grupo: "06 - Destino", nome: "Assinatura Destino", tipo: "assinatura", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 78 },
  { grupo: "06 - Destino", nome: "Assinatura Destino Data", tipo: "timestamp with time zone", obrigatorio: false, deletavel: true, alteravel: true, visivel: true, ordem: 79 },
];

// Função utilitária para gerar o template completo conforme o número de fotos
export function getDefaultVehicleFieldsWithPhotos(numFotos: number) {
  const baseCount = defaultVehicleFieldsBase.length;
  logger.info?.(`[getDefaultVehicleFieldsWithPhotos] baseCount=${baseCount}, numFotos=${numFotos}, fotos começam em ordem ${baseCount + 1}`);
  return [
    ...defaultVehicleFieldsBase,
    ...generatePhotoFields(numFotos, baseCount + 1)
  ];
} 
